import React, { useState, useEffect } from 'react';
import { organizationService } from '../services/organizationService';
import { Organization, OrganizationCreateRequest, OrganizationUpdateRequest, OrganizationSearchRequest, OrganizationStatistics } from '../types/organizations';
import { useUser } from '../context/UserContext';
import { useNavigate, Link } from 'react-router-dom';
import { getStatusBadgeClasses, getSubscriptionBadgeClasses } from '../utils/badgeHelpers';
import OrganizationModal from '../components/OrganizationModal';

// Utility functions
const timeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  return `${Math.floor(diffInSeconds / 2592000)}mo ago`;
};

const Organizations: React.FC = () => {
  const { accessToken, setUserAndToken } = useUser();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState<OrganizationStatistics | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(20);
  const [error, setError] = useState('');

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);

  // Form states
  const [form, setForm] = useState<OrganizationCreateRequest>({
    legal_entity_name: '',
    website: '',
    types: [],
    subscription_status: 'Active',
    subscription_cycle: null,
    subscription_start_date: null,
    stripe_customer_id: null,
    stripe_subscription_id: null,
    primary_contact: {
      first_name: '',
      last_name: '',
      email_address: '',
      phone_number: '',
      country_code: '+65',
      job_title: '',
      department: ''
    }
  });

  const [editForm, setEditForm] = useState<OrganizationUpdateRequest>({
    legal_entity_name: '',
    website: '',
    types: [],
    subscription_status: 'Active',
    subscription_cycle: null,
    subscription_start_date: null,
    stripe_customer_id: null,
    stripe_subscription_id: null,
    primary_contact: {
      first_name: '',
      last_name: '',
      email_address: '',
      phone_number: '',
      country_code: '+65',
      job_title: '',
      department: ''
    }
  });

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [subscriptionFilter, setSubscriptionFilter] = useState('');
  const [availableTypes, setAvailableTypes] = useState<string[]>([]);
  const [subscriptionStatusOptions, setSubscriptionStatusOptions] = useState<string[]>([]);
  const [subscriptionCycleOptions, setSubscriptionCycleOptions] = useState<string[]>([]);
  const [typesLoading, setTypesLoading] = useState(false);

  // Loading states
  const [saving, setSaving] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [editFormError, setEditFormError] = useState('');
  const [deleteError, setDeleteError] = useState('');

  const navigate = useNavigate();

  // Fetch organizations
  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const searchParams: OrganizationSearchRequest = {
        page: currentPage,
        page_size: pageSize,
        legal_entity_name: searchTerm || undefined,
        subscription_status: subscriptionFilter || undefined
      };

      const response = await organizationService.getOrganizations(searchParams, accessToken, setUserAndToken);
      setOrganizations(response.organizations);
      setTotalPages(Math.ceil(response.total / pageSize));
    } catch (err) {
      setError('Failed to fetch organizations');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch statistics
  const fetchStatistics = async () => {
    try {
      const stats = await organizationService.getOrganizationStatistics(accessToken, setUserAndToken);
      setStatistics(stats);
    } catch (err) {
      console.error('Failed to fetch statistics:', err);
    }
  };

  // Fetch organization types
  const fetchOrganizationTypes = async () => {
    try {
      setTypesLoading(true);
      const types = await organizationService.getOrganizationTypes(accessToken, setUserAndToken);
      setAvailableTypes(types);
    } catch (err) {
      console.error('Failed to fetch organization types:', err);
      setAvailableTypes([]);
    } finally {
      setTypesLoading(false);
    }
  };

  // Fetch subscription status options
  const fetchSubscriptionStatusOptions = async () => {
    try {
      const options = await organizationService.getSubscriptionStatusOptions(accessToken, setUserAndToken);
      setSubscriptionStatusOptions(options);
    } catch (err) {
      console.error('Failed to fetch subscription status options:', err);
      setSubscriptionStatusOptions([]);
    }
  };

  // Fetch subscription cycle options
  const fetchSubscriptionCycleOptions = async () => {
    try {
      const options = await organizationService.getSubscriptionCycleOptions(accessToken, setUserAndToken);
      setSubscriptionCycleOptions(options);
    } catch (err) {
      console.error('Failed to fetch subscription cycle options:', err);
      setSubscriptionCycleOptions([]);
    }
  };

  useEffect(() => {
    fetchOrganizations();
    fetchStatistics();
    fetchOrganizationTypes();
    fetchSubscriptionStatusOptions();
    fetchSubscriptionCycleOptions();
  }, [currentPage, searchTerm, statusFilter, subscriptionFilter]);

  // Initialize availableTypes with default values if empty
  const displayTypes = availableTypes.length > 0 ? availableTypes : [];


  // Add new type to form
  const addTypeToForm = (type: string, isEdit: boolean = false) => {
    if (type && !(isEdit ? editForm.types : form.types)?.includes(type)) {
      const updatedTypes = [...(isEdit ? editForm.types || [] : form.types || []), type];
      if (isEdit) {
        setEditForm({ ...editForm, types: updatedTypes });
      } else {
        setForm({ ...form, types: updatedTypes });
      }
    }
  };

  // Remove type from form
  const removeTypeFromForm = (type: string, isEdit: boolean = false) => {
    const updatedTypes = (isEdit ? editForm.types : form.types)?.filter(t => t !== type) || [];
    if (isEdit) {
      setEditForm({ ...editForm, types: updatedTypes });
    } else {
      setForm({ ...form, types: updatedTypes });
    }
  };

  // Handle form submission
  const handleAddOrganization = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.legal_entity_name || !form.subscription_status) {
      setFormError('Legal entity name and subscription status are required.');
      return;
    }
    setSaving(true);
    try {
      const newOrganization = await organizationService.createOrganization(form, accessToken, setUserAndToken);
      
      // Add the new organization to the list
      setOrganizations(prev => [...prev, newOrganization]);
      
      setShowModal(false);
      setForm({
        legal_entity_name: '',
        website: '',
        types: [],
        subscription_status: 'Active',
        subscription_cycle: null,
        subscription_start_date: null,
        stripe_customer_id: null,
        stripe_subscription_id: null,
        primary_contact: {
          first_name: '',
          last_name: '',
          email_address: '',
          phone_number: '',
          country_code: '+65',
          job_title: '',
          department: ''
        }
      });
      setFormError('');
      
      // Refresh the organizations list to ensure we have the latest data including primary contact
      await fetchOrganizations();
      fetchStatistics();
    } catch (err: any) {
      setFormError(err.message || 'Failed to add organization.');
    } finally {
      setSaving(false);
    }
  };

  // Handle edit form submission
  const handleEditOrganization = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOrganization || !editForm.legal_entity_name || !editForm.subscription_status) {
      setEditFormError('Legal entity name and subscription status are required.');
      return;
    }
    setEditSaving(true);
    try {
      const updatedOrganization = await organizationService.updateOrganization(selectedOrganization.id, editForm, accessToken, setUserAndToken);
      
      // Update the organizations list with the new data
      setOrganizations(prev => prev.map(c => c.id === updatedOrganization.id ? updatedOrganization : c));
      
      setShowEditModal(false);
      setSelectedOrganization(null);
      setEditForm({
        legal_entity_name: selectedOrganization.legal_entity_name,
        website: selectedOrganization.website || '',
        types: selectedOrganization.types || [],
        subscription_status: selectedOrganization.subscription_status,
        subscription_cycle: selectedOrganization.subscription_cycle || null,
        subscription_start_date: selectedOrganization.subscription_start_date || null,
        stripe_customer_id: selectedOrganization.stripe_customer_id || null,
        stripe_subscription_id: selectedOrganization.stripe_subscription_id || null,
        primary_contact: selectedOrganization.primary_contact_user ? {
          first_name: selectedOrganization.primary_contact_user.first_name,
          last_name: selectedOrganization.primary_contact_user.last_name,
          email_address: selectedOrganization.primary_contact_user.email_address,
          phone_number: selectedOrganization.primary_contact_user.phone_number || '',
          country_code: selectedOrganization.primary_contact_user.country_code || '+65',
          job_title: selectedOrganization.primary_contact_user.job_title || '',
          department: selectedOrganization.primary_contact_user.department || ''
        } : {
          first_name: '',
          last_name: '',
          email_address: '',
          phone_number: '',
          country_code: '+65',
          job_title: '',
          department: ''
        }
      });
      setEditFormError('');
      
      // Refresh the organizations list to ensure we have the latest data including primary contact
      await fetchOrganizations();
      fetchStatistics();
    } catch (err: any) {
      setEditFormError(err.message || 'Failed to update organization.');
    } finally {
      setEditSaving(false);
    }
  };

  // Handle delete
  const handleDeleteOrganization = async () => {
    if (!selectedOrganization) return;
    setDeleteLoading(true);
    try {
      await organizationService.deleteOrganization(selectedOrganization.id, accessToken, setUserAndToken);
      setOrganizations(prev => prev.filter(c => c.id !== selectedOrganization.id));
      setShowDeleteModal(false);
      setSelectedOrganization(null);
      setDeleteError('');
      fetchStatistics();
    } catch (err: any) {
      setDeleteError(err.message || 'Failed to delete organization.');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Open delete modal
  const openDeleteModal = (organization: Organization) => {
    setSelectedOrganization(organization);
    setShowDeleteModal(true);
    setDeleteError('');
  };

  if (loading && organizations.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg text-slate-900 mb-1 px-2">Manage the users of ARiA</h2>
        </div>
        <button className="bg-[#4a67b7] hover:bg-primary/90 text-white px-6 py-2 rounded-lg flex items-center shadow transition" onClick={() => setShowModal(true)}>
          + Add Organization
        </button>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div className="w-full">
          <div className="overflow-x-auto">
            <div className="flex gap-6 pb-2" style={{ width: '480px', maxWidth: 'calc(100vw - 2rem)' }}>
              {/* Total Customers Card */}
              <div className="bg-white rounded-lg border border-gray-200 p-6 min-w-[200px] flex-shrink-0">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Total Organizations</p>
                    <p className="text-2xl font-bold text-gray-900">{statistics.total_organizations}</p>
                  </div>
                </div>
              </div>

              {/* Dynamic Subscription Status Cards */}
              {subscriptionStatusOptions.map((statusOption) => {
                const statKey = `${statusOption.toLowerCase().replace(/ /g, '_')}_organizations`;
                const count = statistics.subscription_status_stats?.[statKey] || 0;
                
                // Define colors for different status options
                const getStatusColors = (status: string) => {
                  switch (status) {
                    case 'Active': return 'bg-green-100 text-green-600';
                    case 'Design Partner': return 'bg-purple-100 text-purple-600';
                    case 'Pilot Customer': return 'bg-blue-100 text-blue-600';
                    case 'Paused': return 'bg-yellow-100 text-yellow-600';
                    case 'Cancelled': return 'bg-red-100 text-red-600';
                    case 'Discontinued DP': return 'bg-gray-100 text-gray-600';
                    default: return 'bg-slate-100 text-slate-600';
                  }
                };

                const colors = getStatusColors(statusOption);
                
                return (
                  <div key={statusOption} className="bg-white rounded-lg border border-gray-200 p-6 min-w-[200px] flex-shrink-0">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className={`w-8 h-8 ${colors.split(' ')[0]} rounded-lg flex items-center justify-center`}>
                          <svg className={`w-5 h-5 ${colors.split(' ')[1]}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-500">{statusOption}</p>
                        <p className={`text-2xl font-bold ${colors.split(' ')[1]}`}>{count}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}



      {/* Error Display */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-sm border border-slate-100">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="text-lg text-slate-900">All Users of ARiA</h3>
          <div className="flex items-center gap-2">
            <div className="relative">
              <input
                type="text"
                placeholder="Search by legal entity name, industry, or types..."
                className="pl-4 pr-10 py-2 rounded-lg border border-slate-200 bg-slate-50 text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-100 text-sm"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              <span className="absolute right-3 top-2.5 text-slate-400">
                <svg width="16" height="16" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" strokeWidth="2" d="M21 21l-4.35-4.35m2.1-5.4a7.5 7.5 0 11-15 0 7.5 7.5 0 0115 0z"/></svg>
              </span>
            </div>
            <div className="relative">
              <select
                className="pl-4 pr-10 py-2 rounded-lg border border-slate-200 bg-slate-50 text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-100 text-sm appearance-none"
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
              >
                <option value="">All Statuses</option>
                {subscriptionStatusOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
              <span className="pointer-events-none absolute right-3 top-2.5 text-slate-400">
                <svg width="16" height="16" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" strokeWidth="2" d="M6 9l6 6 6-6"/></svg>
              </span>
            </div>
            <div className="relative">
              <select
                className="pl-4 pr-10 py-2 rounded-lg border border-slate-200 bg-slate-50 text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-100 text-sm appearance-none"
                value={subscriptionFilter}
                onChange={e => setSubscriptionFilter(e.target.value)}
              >
                <option value="">All Subscriptions</option>
                {subscriptionCycleOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
              <span className="pointer-events-none absolute right-3 top-2.5 text-slate-400">
                <svg width="16" height="16" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" strokeWidth="2" d="M6 9l6 6 6-6"/></svg>
              </span>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-slate-500 border-b border-slate-100">
                <th className="px-6 py-3 text-left font-semibold w-1/6">ORGANIZATION</th>
                <th className="px-6 py-3 text-left font-semibold w-1/5">PRIMARY CONTACT</th>
                <th className="px-6 py-3 text-left font-semibold w-1/6">TYPES</th>
                <th className="px-6 py-3 text-left font-semibold w-1/6">SUBSCRIPTION</th>
                <th className="px-6 py-3 text-left font-semibold w-1/12">STATUS</th>
                <th className="px-6 py-3 text-left font-semibold w-1/12">CREATED</th>
                <th className="px-6 py-3 text-left font-semibold w-1/12">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-6 py-8 text-center text-slate-400">Loading...</td></tr>
              ) : organizations.length === 0 ? (
                <tr><td colSpan={7} className="px-6 py-8 text-center text-slate-400">No organizations found.</td></tr>
              ) : organizations.map((organization) => (
                <tr key={organization.id} className="border-b border-slate-50 hover:bg-slate-50 transition">
                  <td className="px-6 py-4 text-left">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 font-bold text-base flex-shrink-0">
                        {organization.legal_entity_name.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-slate-900 text-left">
                          <Link
                            to={`/organizations/${organization.id}`}
                            className="text-blue-600 hover:text-blue-900 hover:underline text-left"
                          >
                            {organization.legal_entity_name}
                          </Link>
                        </div>
                      </div>
                    </div>
                  </td>

                  <td className="px-6 py-4 text-left">
                    {organization.primary_contact_user ? (
                      <div>
                        <div className="font-medium text-gray-900">
                          {organization.primary_contact_user.first_name} {organization.primary_contact_user.last_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {organization.primary_contact_user.email_address}
                        </div>
                        {organization.primary_contact_user.job_title && (
                          <div className="text-sm text-gray-500">
                            {organization.primary_contact_user.job_title}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-500">No primary contact</span>
                    )}
                  </td>

                  <td className="px-6 py-4 text-left">
                    <div className="flex flex-wrap gap-1 max-w-48">
                      {organization.types?.map((type) => (
                        <span
                          key={type}
                          className="bg-slate-100 text-slate-700 px-2 py-1 rounded-full text-xs whitespace-nowrap flex-shrink-0"
                        >
                          {type}
                        </span>
                      )) || '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-left">
                    <div>
                      <span className={getSubscriptionBadgeClasses(organization.subscription_status)}>
                        {organization.subscription_status}
                      </span>
                      {organization.subscription_cycle && (
                        <div className="text-slate-500 text-xs mt-1">
                          {organization.subscription_cycle}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-left">
                    <span className={getStatusBadgeClasses(organization.status)}>
                      {organization.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-left text-slate-500">{timeAgo(organization.created_at)}</td>
                  <td className="px-6 py-4 text-left whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          setSelectedOrganization(organization);
                          setEditForm({
                            legal_entity_name: organization.legal_entity_name,
                            website: organization.website || '',
                            types: organization.types || [],
                            subscription_status: organization.subscription_status,
                            subscription_cycle: organization.subscription_cycle || null,
                            subscription_start_date: organization.subscription_start_date || null,
                            stripe_customer_id: organization.stripe_customer_id || null,
                            stripe_subscription_id: organization.stripe_subscription_id || null,
                            primary_contact: organization.primary_contact_user ? {
                              first_name: organization.primary_contact_user.first_name,
                              last_name: organization.primary_contact_user.last_name,
                              email_address: organization.primary_contact_user.email_address,
                              phone_number: organization.primary_contact_user.phone_number || '',
                              country_code: organization.primary_contact_user.country_code || '+65',
                              job_title: organization.primary_contact_user.job_title || '',
                              department: organization.primary_contact_user.department || ''
                            } : {
                              first_name: '',
                              last_name: '',
                              email_address: '',
                              phone_number: '',
                              country_code: '+65',
                              job_title: '',
                              department: ''
                            }
                          });
                          setShowEditModal(true);
                        }}
                        className="text-indigo-600 hover:text-indigo-900 p-1 rounded hover:bg-indigo-50"
                        title="Edit"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => openDeleteModal(organization)}
                        className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                        title="Delete"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-3 text-slate-500 text-sm border-t border-slate-100">
          Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, organizations.length)} of {organizations.length} results
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-6">
          <nav className="flex items-center space-x-2 bg-white rounded-lg border border-slate-200 px-4 py-2 shadow-sm">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 text-sm font-medium text-slate-500 hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <span className="px-3 py-2 text-sm text-slate-700">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-2 text-sm font-medium text-slate-500 hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </nav>
        </div>
      )}



            {/* Add Organization Modal */}
      <OrganizationModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleAddOrganization}
        title="Add New User of ARiA"
        form={form}
        setForm={setForm}
        saving={saving}
        formError={formError}
        typesLoading={typesLoading}
        displayTypes={displayTypes}
        subscriptionStatusOptions={subscriptionStatusOptions}
        subscriptionCycleOptions={subscriptionCycleOptions}
        addTypeToForm={addTypeToForm}
        removeTypeFromForm={removeTypeFromForm}
      />

      {/* Edit Organization Modal */}
      <OrganizationModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSubmit={handleEditOrganization}
        title="Edit Organization"
        form={editForm}
        setForm={setEditForm}
        isEdit={true}
        saving={editSaving}
        formError={editFormError}
        typesLoading={typesLoading}
        displayTypes={displayTypes}
        subscriptionStatusOptions={subscriptionStatusOptions}
        subscriptionCycleOptions={subscriptionCycleOptions}
        addTypeToForm={addTypeToForm}
        removeTypeFromForm={removeTypeFromForm}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedOrganization && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Delete Organization</h3>
              <p className="text-sm text-gray-500 mb-4">
                Are you sure you want to delete "{selectedOrganization.legal_entity_name}"? This action cannot be undone.
              </p>
              {deleteError && (
                <div className="mb-4 text-red-600 text-sm">{deleteError}</div>
              )}
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteOrganization}
                  disabled={deleteLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteLoading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Organizations; 